import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { toast } from "react-toastify"
import { Route, Router, Switch } from 'react-router-dom';

// Internal library
import { CacheBuster, history } from 'dsilo-ui-components';

// Pages
import Org from './Pages/Org/Org';
import Profile from './Pages/Profile';
import MFAComponent from './Pages/MFA';
import SAMLComponent from './Pages/SAML';
import NotFound from './Pages/NotFound';
import LoginComponent from './Pages/Login';
import AppLoading from './Pages/AppLoading';
import SignupComponent from './Pages/Signup';
import ContactDsilo from './Pages/Contactus';
import VerifyEmail from './Pages/VerifyEmail';
import AppsDashboard from './Pages/AppsDashboard';
import Contracts from './Pages/Contracts/Contracts'; //TODO: need to move this components css into ui-library.
import AppHeader from './Pages/AppsDashboard/AppHeader';
// import Challenges from './Pages/Challenges/Challenges';
import ResetPassword from './Pages/ResetPassword/ResetPassword';

// Internal components
import { Auth } from 'components/auth';
import SilentSSO from './components/SilentSSO';
import LayoutWithTheme from './components/Layout/LayoutWithTheme';
import ProcureGpt from './components/ProcureGpt';
import CookieConsent from './components/CookieConsent';
import SelfServiceModels from './components/SelfServiceAi/models';
import PreAppLoading from './components/PreAppLoading/PreAppLoading';
import SelfServiceModelDetails from './components/SelfServiceAi/modelDetails';

// jwtService
import jwtService from 'jwtService';

// custom hooks
import useUserActivity from './hooks/useUserActivity';
import useCrossTabLogout from './hooks/useCrossTabLogout';
import { triggerCrossTabLogout } from './hooks/useCrossTabLogout/triggerCrossTabLogout';
// Session Support
import SessionSupport from './components/SessionSupport';

// Redux store actions
import { loadAppconfig, ssoReset } from './store/actions';

// Routes
import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';

import 'styles/styles.scss';

import metaJson from '../assets/meta.json';
global.appVersion = metaJson.version;
global.GITHASH = metaJson.hash;

const ProcureChatGpt = () => {
	return (
		<AppHeader showBgColor>
			<ProcureGpt />
		</AppHeader>
	)
}

export const App = (props) => {
	const { user } = props
	const [trigger, setTrigger] = useState(null) // null or timestamp for triggering SSO check
	const [e401, setE401] = useState(false)

	// Clear toast notifications on route change to prevent memory leaks
	// Toast can hold references to React components passed to it
	useEffect(() => {
		const unlisten = history.listen(() => {
			// Dismiss all toasts when route changes to prevent memory leaks
			// from React components rendered inside toast
			toast.dismiss();
		});

		return () => {
			unlisten();
		};
	}, []);

	useEffect(() => {
		if (location.protocol !== 'https:' && location.hostname !== "localhost") {
			location.replace(`https:${location.href.substring(location.protocol.length)}`);
			return
		}

		// To verify user session, when user open the browser tab
		// Silent SSO Triggered - use a unique trigger value to ensure useEffect fires
		const handleVisibilityChange = () => {
			const ml = localStorage.getItem('m-l')
			if (document.visibilityState === "visible" && !ml) {
				console.log("Tab is visible. Triggering SSO check.");
				// Use timestamp to ensure each visibility triggers a new check
				setTrigger(Date.now())
			}
			// Don't set to false on invisible - let ssoExpired control the flow
		};

		// To verify user session, when user open the browser tab
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Listen for Silent SSO completion from iframe
		const handleSsoMessage = (event) => {
			if (event.data === "iframeRefreshed") {
				console.log("Silent SSO completed successfully (message from iframe)");
				props.ssoReset();
			}
		};
		window.addEventListener("message", handleSsoMessage);

		// Cleanup on unmount to prevent memory leaks
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("message", handleSsoMessage);
		};
	}, [])

	// const handleInactivity = () => {
	// 	const isAdLogin = localStorage.getItem('isAdLogin') || '';
	// 	const isSsoLogin = localStorage.getItem('isSsoLogin') || '';
	// 	let ssoConfig = null;
	// 	let userEmailReport = localStorage.getItem('userEmailReport')
	// 	if (userEmailReport) {
	// 		let emailCheckData = JSON.parse(window.atob(userEmailReport))
	// 		ssoConfig = emailCheckData?.ssoConfig
	// 	}
	// 	console.log("isSsoLogin , isAdLogin", isAdLogin !== 'true' && isSsoLogin !== 'true', isAdLogin, isSsoLogin)
	// 	// Don't call refresh token in both scenarios
	// 	if (isAdLogin !== 'true' && (isSsoLogin !== 'true' || (isSsoLogin == 'true' && ssoConfig?.system === 'pingID'))) {
	// 		toast.info('Session expired due to inactivity.', { toastId: 'logout-session' })
	// 		// TODO: Clear session data, redirect to login, etc.
	// 		triggerCrossTabLogout(); // Notify other tabs
	// 		const jwt_access_token = localStorage.getItem('jwt_access_token')
	// 		if(jwt_access_token)
	// 			jwtService.logout()
	// 	}
	// };

	// const lastActivity = useUserActivity(handleInactivity);

	// useCrossTabLogout(() => {
	// 	// Same logout logic (without sending the message again to avoid loops)
	// 	const jwt_access_token = localStorage.getItem('jwt_access_token')
	// 	if(jwt_access_token)
	// 		jwtService.logout()
	// });


	// Invoked only when SSO is expired (401 error)
	// This will set the trigger to force Silent SSO
	useEffect(() => {
		console.log("SSO Expired", user.ssoExpired)
		if (user.ssoExpired) {
			setTrigger(Date.now()) // Trigger SSO check
			setE401(true)
		} else {
			setE401(false)
			// Don't reset trigger - let the interval handle normal refreshes
		}
	}, [user.ssoExpired])

	const publicPath = process.env.REACT_APP_PUBLIC_PATH ? process.env.REACT_APP_PUBLIC_PATH + '/' : ''
	const initUrl = `${publicPath}${':appid'}`

	// Memoize route paths to prevent new array references on every render
	const layoutPaths = useMemo(() => [
		'/', 
		`/${initUrl}`, 
		`/${initUrl}/page/:page`,
		`/${initUrl}/page/:page/:id`,
		`/${initUrl}/page/:page/:id/sd/:sdid`,
		`/${initUrl}/page/:page/:id/pi/:pi`,
		`/${initUrl}/page/:page/:id/pi/:pi/rpp/:rpp`,
		`/${initUrl}/page/:page/:pagetype/:id`
	], [initUrl]);

	const profilePaths = useMemo(() => [`/${initUrl}/profile`, '/profile'], [initUrl]);
	const aiAssistantPaths = useMemo(() => [`/${initUrl}/aiAssistant`, '/aiAssistant'], [initUrl]);
	const orgPaths = useMemo(() => [`/${initUrl}/org`, '/org'], [initUrl]);
	const selfServiceModelPaths = useMemo(() => [`/${initUrl}/selfserviceai/model/:id`], [initUrl]);
	const selfServiceModelsPaths = useMemo(() => [`/${initUrl}/selfserviceai/models`], [initUrl]);

	const secretKey = process.env.REACT_APP_CRYPT_SECRET_KEY || '';
	return (
		<React.Fragment>
			<SessionSupport />
			<CacheBuster publicPath={process.env.REACT_APP_PUBLIC_PATH} global={global} process={process}>
				{({ loading, isLatestVersion, refreshCacheAndReload }) => {
					if (loading) return <PreAppLoading />;
					if (!loading && !isLatestVersion) {
						// You can decide how and when you want to force reload
						refreshCacheAndReload();
						return <PreAppLoading />
					}
					return <><div className={"app-cvm"} >
						<ToastContainer hideProgressBar={true} />
						<CookieConsent />
						<SilentSSO secretKey={secretKey} restart={trigger} e401={e401} />
						<Auth>
							<Router history={history}>
								<Switch>
									<PublicRoute path={`/${publicPath}login`} exact component={LoginComponent} />
									<PublicRoute path={`/${publicPath}signup`} exact component={SignupComponent} />
									<PublicRoute path={`/${publicPath}mfaaad`} exact component={MFAComponent} />
									<PublicRoute path={`/${publicPath}saml/acs`} exact component={SAMLComponent} />
									<Route path={`/${publicPath}resetpassword/:token`} exact component={ResetPassword} />
									<PublicRoute path={`/${publicPath}forgotpassword`} exact component={ResetPassword} />
									<PublicRoute path={`/${publicPath}verifypage/:token`} exact component={VerifyEmail} />
									<PrivateRoute path={profilePaths} exact component={Profile} />
									<PrivateRoute path={aiAssistantPaths} exact component={ProcureChatGpt} />
									<PrivateRoute path={orgPaths} exact component={Org} />
									<PrivateRoute path="/apploading" exact component={AppLoading} />
									<PrivateRoute path="/health" exact component={AppLoading} />
									<PrivateRoute path="/apps" exact component={AppsDashboard} />
									<PrivateRoute path="/contact-dsilo" exact component={ContactDsilo} />
									{/* {props.members && <Switch> */}
									<PrivateRoute path={layoutPaths} exact component={LayoutWithTheme} />
									<PrivateRoute path={selfServiceModelPaths} exact component={SelfServiceModelDetails} />
									<PrivateRoute path={selfServiceModelsPaths} exact component={SelfServiceModels} />
									<Route component={NotFound} />
									{/* {props.memberSuccess && <Route component={NotFound} /> } */}
								</Switch>
							</Router>
						</Auth>
					</div></>
				}}
			</CacheBuster>
		</React.Fragment>
	);
};

const mapDispatchToProps = {
	loadAppconfig,
	ssoReset
};

const mapStateToProps = state => {
	return {
		appConfig: state.appConfig.app,
		members: state.appConfig.members,
		memberSuccess: state.appConfig.memberSuccess,
		apps: state.app.app,
		login: state.login,
		user: state.user,
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
